'use client';

import { AlertCircle, CheckCircle2, Loader2, MinusCircle, Settings } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { useToast } from '@/components/ui/use-toast';
import { useBulkUpdateStoreSettings } from '@/app/stores/hooks/use-bulk-update-store-settings';
import type { Store } from '@/lib/api/stores';
import type { BulkSettingsUpdateResult } from '@/app/stores/utils/bulk-update-settings';
import { cn } from '@/lib/utils';

type BulkUpdateSettingsModalProps = {
  stores: Store[];
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
};

type ModalStep = 'form' | 'result';
type ResultStatus = 'success' | 'partial' | 'failed' | 'skipped';

const getFailureReason = (reason: string, t: (key: string) => string) => {
  if (reason === 'currency_update_in_progress') return t('table.settingsDialog.failureInProgress');
  if (reason === 'inngest_unavailable') return t('table.settingsDialog.failureInngest');
  if (reason === 'unknown_error') return t('table.settingsDialog.failureUnknown');
  return reason;
};

const getResultStatus = (result: BulkSettingsUpdateResult): ResultStatus => {
  if (result.failed.length > 0 && result.updated === 0) return 'failed';
  if (result.failed.length > 0) return 'partial';
  if (result.updated === 0 && result.skipped > 0) return 'skipped';
  return 'success';
};

type ResultStatusBannerProps = {
  status: ResultStatus;
  t: (key: string, options?: Record<string, unknown>) => string;
};

const resultBannerStyles: Record<ResultStatus, string> = {
  success: 'border-green-200 bg-green-50 text-green-800',
  partial: 'border-amber-200 bg-amber-50 text-amber-900',
  failed: 'border-destructive/30 bg-destructive/10 text-destructive',
  skipped: 'border-muted bg-muted/50 text-muted-foreground',
};

const ResultStatusBanner = ({ status, t }: ResultStatusBannerProps) => {
  const Icon = status === 'failed' || status === 'partial' ? AlertCircle : CheckCircle2;
  return (
    <div className={cn('flex items-start gap-3 rounded-md border p-4', resultBannerStyles[status])}>
      <Icon className="mt-0.5 h-5 w-5 shrink-0" />
      <div className="space-y-1">
        <p className="text-sm font-medium">{t(`table.settingsDialog.resultBanner.${status}.title`)}</p>
        <p className="text-xs opacity-90">{t(`table.settingsDialog.resultBanner.${status}.description`)}</p>
      </div>
    </div>
  );
};

type BulkSettingsResultPanelProps = {
  result: BulkSettingsUpdateResult;
  t: (key: string, options?: Record<string, unknown>) => string;
};

const BulkSettingsResultPanel = ({ result, t }: BulkSettingsResultPanelProps) => {
  const status = getResultStatus(result);
  return (
    <div className="space-y-4 px-6 py-4">
      <ResultStatusBanner status={status} t={t} />
      {result.updated > 0 ? (
        <div className="flex items-start gap-2 text-sm text-green-600">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{t('table.settingsDialog.resultUpdated', { count: result.updated })}</span>
        </div>
      ) : null}
      {result.skipped > 0 ? (
        <div className="text-muted-foreground flex items-start gap-2 text-sm">
          <MinusCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{t('table.settingsDialog.resultSkipped', { count: result.skipped })}</span>
        </div>
      ) : null}
      {result.failed.length > 0 ? (
        <div className="space-y-2">
          <div className="text-destructive flex items-start gap-2 text-sm font-medium">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{t('table.settingsDialog.resultFailed', { count: result.failed.length })}</span>
          </div>
          <ul className="bg-muted/50 max-h-36 space-y-1.5 overflow-y-auto rounded-md border p-3 text-xs">
            {result.failed.map((item) => (
              <li key={item.storeId}>
                <span className="font-medium">{item.storeName}</span>
                <span className="text-muted-foreground"> — {getFailureReason(item.reason, t)}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
};

type MultipleSubscriptionsFieldProps = {
  value: boolean;
  disabled: boolean;
  onChange: (enabled: boolean) => void;
  t: (key: string) => string;
};

const activeToggleClass =
  'min-w-14 px-3 data-[state=on]:border-primary data-[state=on]:bg-primary data-[state=on]:text-primary-foreground hover:data-[state=on]:bg-primary/90';

const MultipleSubscriptionsField = ({ value, disabled, onChange, t }: MultipleSubscriptionsFieldProps) => (
  <div className="flex items-center justify-between gap-4 rounded-md border p-3">
    <div className="space-y-1">
      <Label className="text-sm font-medium">{t('form.multipleSubscriptions')}</Label>
      <p className="text-muted-foreground text-xs">{t('form.multipleSubscriptionsDescription')}</p>
    </div>
    <ToggleGroup
      type="single"
      variant="outline"
      value={value ? 'on' : 'off'}
      disabled={disabled}
      onValueChange={(next) => {
        if (next === 'on' || next === 'off') onChange(next === 'on');
      }}
    >
      <ToggleGroupItem value="on" className={activeToggleClass}>
        {t('table.settingsDialog.on')}
      </ToggleGroupItem>
      <ToggleGroupItem value="off" className={activeToggleClass}>
        {t('table.settingsDialog.off')}
      </ToggleGroupItem>
    </ToggleGroup>
  </div>
);

export const BulkUpdateSettingsModal = ({
  stores,
  open,
  onClose,
  onSuccess,
}: BulkUpdateSettingsModalProps) => {
  const { t } = useTranslation('stores');
  const { t: tCommon } = useTranslation('common');
  const { toast } = useToast();
  const bulkUpdateMutation = useBulkUpdateStoreSettings();
  const [multipleSubscriptionsEnabled, setMultipleSubscriptionsEnabled] = React.useState(true);
  const [step, setStep] = React.useState<ModalStep>('form');
  const [result, setResult] = React.useState<BulkSettingsUpdateResult | null>(null);

  React.useEffect(() => {
    if (!open) return;
    setMultipleSubscriptionsEnabled(true);
    setStep('form');
    setResult(null);
  }, [open]);

  const handleApply = async () => {
    try {
      const bulkResult = await bulkUpdateMutation.mutateAsync({
        stores,
        multipleSubscriptionsPerCustomerEnabled: multipleSubscriptionsEnabled,
      });
      setResult(bulkResult);
      setStep('result');
      if (bulkResult.updated > 0) onSuccess?.();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : t('table.settingsDialog.error');
      toast({ title: t('table.settingsDialog.error'), description: message, variant: 'destructive' });
    }
  };

  const handleClose = () => {
    setStep('form');
    setResult(null);
    onClose();
  };

  const isPending = bulkUpdateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && !isPending && handleClose()}>
      <DialogContent className="max-w-md gap-0 overflow-hidden p-0">
        <DialogHeader className="space-y-1 border-b px-6 py-5 text-left">
          <div className="flex items-center gap-2">
            <div className="bg-primary/10 text-primary flex h-8 w-8 items-center justify-center rounded-md">
              <Settings className="h-4 w-4" />
            </div>
            <DialogTitle>
              {step === 'result' ? t('table.settingsDialog.resultTitle') : t('table.settingsDialog.title')}
            </DialogTitle>
          </div>
          <DialogDescription>
            {step === 'result'
              ? t('table.settingsDialog.resultDescription')
              : t('table.settingsDialog.description', { count: stores.length })}
          </DialogDescription>
        </DialogHeader>

        {step === 'form' ? (
          <div className="space-y-3 px-6 py-4">
            <p className="text-muted-foreground text-xs">{t('table.settingsDialog.fieldsHint')}</p>
            <MultipleSubscriptionsField
              value={multipleSubscriptionsEnabled}
              disabled={isPending}
              onChange={setMultipleSubscriptionsEnabled}
              t={t}
            />
          </div>
        ) : (
          result && <BulkSettingsResultPanel result={result} t={t} />
        )}

        <DialogFooter className="border-t px-6 py-4">
          {step === 'form' ? (
            <>
              <Button variant="outline" onClick={handleClose} disabled={isPending}>
                {tCommon('cancel')}
              </Button>
              <Button onClick={() => void handleApply()} disabled={isPending || stores.length === 0}>
                {isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                {isPending
                  ? t('table.settingsDialog.applying', { count: stores.length })
                  : t('table.settingsDialog.confirm')}
              </Button>
            </>
          ) : (
            <Button onClick={handleClose}>{t('table.settingsDialog.done')}</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
